const {V2} = require("../../../shared/Vector");
const _ = require("lodash");
const CanvasMath = require("../canvasmath/CanvasMath");
const FastCanvasMath = require("../canvasmath/FastCanvasMath");
const Font = require("./Font");
const Math2D = require("./Math2D");
const CanvasUtil = require("./CanvasUtil");
const AssetsUtil = require("./AssetsUtil");
const ColorUtil = require("./ColorUtil");
const MultiplePattern = require("./MultiplePattern");
const DrawRectUtil = require("./DrawRectangleUtil");
const ImageCache = require("./ImageCache");
const ArrowDraw = require("./ArrowDraw");
const browser = require("detect-browser").detect();

const defaultColors = ColorUtil.getCurrentScheme();

const drawRoundedRect = DrawRectUtil.drawRoundedRect;

// This is a function to draw a regular shape using canvas
// This function should take in x,y position of shape's center
// numSides is the number of sides of the regular shape (0 is circle)
// size is the radius or distance from center to vertex of shape
// rotation is in degrees of how rotated the shape is
// shaded is a string hex for the color of the shading of the shape
const drawRegularShape = function(ctx, x, y, numSides, size, rotation, shaded) {
	const rot = (rotation * Math.PI) / 180;

	ctx.save();
	if (numSides === 0) {
		ctx.beginPath();
		ctx.arc(x, y, size, 0, 2 * Math.PI);
		ctx.closePath();
	} else if (numSides >= 3) {
		size = size / (2 * Math.sin(Math.PI / numSides));
		ctx.beginPath();
		ctx.moveTo(x + size * Math.cos(rot), y + size * Math.sin(rot));

		for (let v = 1; v <= numSides; v++) {
			ctx.lineTo(
				x + size * Math.cos(rot + (v * 2 * Math.PI) / numSides),
				y + size * Math.sin(rot + (v * 2 * Math.PI) / numSides)
			);
		}

		ctx.closePath();
	} else {
		return;
	}

	if (shaded) {
		ctx.fillStyle = shaded;
		ctx.fill();
	}

	ctx.stroke();
	ctx.restore();
};

//This function draws batex in a regular shape
const drawInShape = function(
	ctx,
	x,
	y,
	batex,
	numSides,
	size,
	rotation,
	shaded
) {
	drawRegularShape(ctx, x, y, numSides, size, rotation, shaded);
	const props = {batex: batex, rect: [x - size, y - size, 2 * size, 2 * size]};
	CanvasMath.drawInRect(ctx, props);
};

//Draws a triangle
const drawTriangle = function(ctx, points) {
	ctx.save();
	ctx.beginPath();
	ctx.moveTo(points[0]);
	ctx.lineTo(points[1]);
	ctx.lineTo(points[2]);
	ctx.stroke();
	ctx.restore();
};

//Draws a 5 point star
const drawStar = function(
	ctx,
	x,
	y,
	size,
	rotation,
	fillStar,
	contextSettings
) {
	ctx.save();
	if (!_.isEmpty(contextSettings)) {
		_.each(contextSettings, (value, key) => {
			ctx[key] = value;
		});
	}
	const alpha = (2 * Math.PI) / 10;
	const center = [x, y];
	ctx.beginPath();
	for (let i = 11; i !== 0; i--) {
		const r = i % 2 ? size : (size * Math.sqrt(3) * 2) / 9;
		const omega = alpha * i;
		ctx.lineTo(
			r * Math.sin(omega) + center[0],
			r * Math.cos(omega) + center[1]
		);
	}
	ctx.closePath();
	if (fillStar) {
		ctx.fill();
	}
	ctx.stroke();
	ctx.restore();
};

const drawTable = function(ctx, data, x, y, boxSize, style, boxStyles) {
	const totalRows = data.length;
	const totalColumns = data[0].length;
	const boxPadding = boxStyles.boxPadding || 0;
	const headerColor = boxStyles.headerColor || defaultColors.lightGrayFill;
	// drawing the box borders and the text inside each box and shading
	for (let i = 0; i < totalRows; i++) {
		for (let j = 0; j < totalColumns; j++) {
			ctx.save();
			ctx.beginPath();
			if (style === "timestable") {
				if (i === 0 && j === 0) {
					ctx.font = Font.incSize(ctx.font, 30);
				} else if (i === 0 || j === 0) {
					ctx.fillStyle = headerColor;
					ctx.fillRect(x + j * boxSize, y + i * boxSize, boxSize, boxSize);
					ctx.fillStyle = defaultColors.defaultText;
					if (boxStyles.shadeRow === i || boxStyles.shadeColumn === j) {
						ctx.font = Font.makeBold(ctx.font);
					}
				} else {
					if (
						boxStyles.rowColShadeColor &&
						(boxStyles.shadeRow === i || boxStyles.shadeColumn === j)
					) {
						ctx.fillStyle = boxStyles.rowColShadeColor;
						ctx.fillRect(x + j * boxSize, y + i * boxSize, boxSize, boxSize);
					}
					if (boxStyles.textColor) {
						ctx.fillStyle = boxStyles.textColor;
					}
				}
				ctx.rect(x + j * boxSize, y + i * boxSize, boxSize, boxSize);
			}
			if (style === "sumsquares") {
				if (i > 0 && j > 0) {
					ctx.rect(x + j * boxSize, y + i * boxSize, boxSize, boxSize);
				}
			}
			if (data[i][j] !== null) {
				CanvasMath.drawInRect(ctx, {
					batex: data[i][j].toString(),
					rect: [
						x + j * boxSize + boxPadding / 2,
						y + i * boxSize + boxPadding / 2,
						boxSize - boxPadding,
						boxSize - boxPadding,
					],
				});
			}
			ctx.stroke();
			ctx.restore();
		}
	}
};

const getNonDraggingShadowSizeRatio = function() {
	return 1 / 6;
};

const getDraggingShadowSize = function(rect) {
	return Math.min(rect[2], rect[3] / 2, 20);
};

const drawDraggingRect = function(ctx, rect, props = {}) {
	const colors = props.colors || defaultColors;
	let size = getDraggingShadowSize(rect);
	if (props.shadowMultiplier) {
		size *= props.shadowMultiplier;
	}
	drawDroppedDraggable(ctx, rect, props);
	DrawRectUtil.drawOutsetRect(ctx, rect, {
		blurOffset: size,
		blurSize: size / 1.2,
		shadowColor: colors.defaultShadow,
	});
};

const drawDraggable = function(ctx, rect, props = {}) {
	const colors = props.colors || defaultColors;
	const originalSize = getDraggingShadowSize(rect);
	let size = originalSize;
	if (props.shadowMultiplier) {
		size = originalSize * props.shadowMultiplier;
	}
	drawDroppedDraggable(ctx, rect, props);
	DrawRectUtil.drawOutsetRect(ctx, rect, {
		blurOffset: size * getNonDraggingShadowSizeRatio(),
		blurSize: originalSize,
		shadowColor: colors.defaultShadow,
	});
};

const drawHoveringDroppable = function(ctx, rect, props = {}) {
	const size = getDraggingShadowSize(rect);
	const colors = props.colors || defaultColors;
	ctx.save();
	ctx.setShadowBlur(size);
	ctx.shadowColor = colors.defaultShadow;
	ctx
		.bp()
		.rect(rect)
		.stroke(colors.defaultShadow)
		.fill(colors.defaultShadow);
	ctx.restore();
};

const drawDroppedDraggable = function(ctx, rect, props = {}) {
	const colors = props.colors || defaultColors;
	const outlineColor = props.outlineColor || colors.rectOutline;
	ctx.save();
	// Need lineWidth * 2 to account for filling in white background.
	ctx.lineWidth = props.lineWidth || ctx.lineWidth * 2;
	ctx
		.bp()
		.rect(rect)
		.stroke(outlineColor)
		.fill(colors.defaultFill);
	ctx.restore();
};

const drawShadowContent = function(ctx, rect, props = {}) {
	const {content, shadowBlur, shadowColor} = props;
	const colors = props.colors || defaultColors;
	ctx.save();
	ctx.setShadowBlur(shadowBlur);
	ctx.shadowColor = shadowColor;
	ctx.fillStyle = colors.defaultShadow;
	ctx.strokeStyle = colors.defaultShadow;
	if (props.moreBlack) {
		ctx.fillStyle = colors.defaultDarkShadow;
		ctx.strokeStyle = colors.defaultDarkShadow;
	}
	if (_.isString(content)) {
		CanvasMath.drawInRect(ctx, {
			batex: content,
			rect: rect,
			fontInfo: props.fontInfo,
		});
	}
	ctx.restore();
};

/*
	glow can be drawn for a circle or square.
	the circle shape glow is indicated by setting props.isCircle to true
	for square - rect defines [x, y, w, h]
	for circle - rect defines [centerX, centerY, radius]
*/
const drawGlow = function(ctx, rect, props = {}) {
	const colors = props.colors || defaultColors;
	const isCircle = props.isCircle;
	const characteristicLength = isCircle ? rect[2] : Math.min(rect[2], rect[3]);
	ctx.save();
	const fillColor = props.fillColor || colors.defaultFill;
	const shadowColor = props.shadowColor || colors.defaultShadow;
	const blurSize = props.blurSize || Math.min(characteristicLength / 2, 20);
	ctx.setShadowBlur(blurSize * 4);
	ctx.shadowColor = shadowColor;
	ctx.bp();
	if (isCircle) {
		ctx.arc(...rect, 0, 2 * Math.PI);
	} else {
		ctx.rect(rect);
	}
	ctx.fill(fillColor);
	ctx.restore();
};

const drawInnerGlow = function(ctx, rect, props = {}) {
	const colors = props.colors || defaultColors;
	ctx.save();
	ctx.bp().rect(rect);
	ctx.clip();
	DrawRectUtil.whiteOutAfterClip(ctx);
	const fillColor = props.fillColor || colors.defaultFill;
	const shadowColor = props.shadowColor || colors.defaultShadow;
	const blurSize =
		props.blurSize || Math.min(Math.min(rect[2], rect[3]) / 2, 20);
	ctx.setShadowBlur(blurSize * 4);
	ctx.shadowColor = shadowColor;
	ctx
		.bp()
		.rect(rect)
		.fill(fillColor);
	ctx.restore();
};

// Draws an X in a given rect, defaults to deletionX color
// Another version of drawX also exists in GriddyDraw, with diff offset/rect
const drawX = function(ctx, rect, props = {}) {
	ctx.lineCap = "round";
	ctx.lineWidth = props.lineWidth || CanvasUtil.getOptimalLineWidth(ctx);
	const color = props.color || defaultColors.deletionX;
	ctx
		.bp()
		.M(rect[0], rect[1])
		.L(rect[0] + rect[2], rect[1] + rect[3])
		.M(rect[0] + rect[2], rect[1])
		.L(rect[0], rect[1] + rect[3])
		.stroke(color);
};

const drawXWithOffset = function(ctx, rect, props = {}) {
	const dim = rect[2];
	const offset = props.offset || dim / 6;
	const lineWidth = dim / 15;
	const cx = rect[0] + dim / 2;
	const cy = rect[1] + dim / 2;
	ctx.save();
	ctx.beginPath();
	ctx.strokeStyle = props.strokeColor || defaultColors.defaultText;
	ctx.lineWidth = lineWidth;
	ctx.moveTo(cx - offset, cy - offset);
	ctx.lineTo(cx + offset, cy + offset);
	ctx.moveTo(cx + offset, cy - offset);
	ctx.lineTo(cx - offset, cy + offset);
	ctx.stroke();
	ctx.restore();
};

//Draws a toothpick given props which must contain endpoints [[x1,y1],[x2,y2]], isSelected true or false, and isCorrect true false or null
const drawToothpick = function(context, props = {}) {
	const x1 = props.endpoints[0];
	const y1 = props.endpoints[1];
	const x2 = props.endpoints[2];
	const y2 = props.endpoints[3];
	const v = [(x2 - x1) / 8, (y2 - y1) / 8];
	const w = [(y2 - y1) / 48, -(x2 - x1) / 48];
	const gap = [(x2 - x1) / 24, (y2 - y1) / 24];
	const colors = props.colors || defaultColors;
	context.save();
	context.lineWidth = 1;
	context.lineJoin = "round";
	context.beginPath();
	context.moveTo(x1 + gap[0], y1 + gap[1]);
	context.lineTo(x1 + gap[0] + v[0] + w[0], y1 + gap[1] + v[1] + w[1]);
	context.lineTo(x2 - gap[0] - v[0] + w[0], y2 - gap[1] - v[1] + w[1]);
	context.lineTo(x2 - gap[0], y2 - gap[1]);
	context.lineTo(x2 - gap[0] - v[0] - w[0], y2 - gap[1] - v[1] - w[1]);
	context.lineTo(x1 + gap[0] + v[0] - w[0], y1 + gap[1] + v[1] - w[1]);
	context.closePath();
	if (props.isSelected) {
		context.shadowColor = colors.inputContrastFill;
		context.setShadowBlur(Math.max(x2 - x1, y2 - y1) / 8);
		context.shadowOffsetX = 0;
		context.shadowOffsetY = 0;
	}
	let correctColor;
	if (props.isCorrect === true) {
		correctColor = props.isRemoved
			? colors.correctFillLight
			: colors.correctFill;
		context.strokeStyle = correctColor;
		context.fillStyle = correctColor;
	} else if (props.isCorrect === false) {
		correctColor = props.isRemoved
			? colors.incorrectFillLight
			: colors.incorrectFill;
		context.strokeStyle = correctColor;
		context.fillStyle = correctColor;
	} else if (props.isMoved) {
		context.strokeStyle = colors.toothpick;
		context.fillStyle = colors.toothpick;
	} else if (props.isRemoved) {
		context.strokeStyle = colors.toothpickRemoved;
		context.fillStyle = colors.toothpickRemoved;
	}
	if (props.forcedColor) {
		context.strokeStyle = props.forcedColor;
		context.fillStyle = props.forcedColor;
	}
	context.fill();
	context.stroke();
	context.restore();
};

const drawHorizontalDomino = function(context, props) {
	const cS = ColorUtil.getCurrentScheme();
	const usedColor = cS.lightGrayFill;
	const x = props.x;
	const y = props.y;
	const w = props.w;
	const h = props.h;
	context.save();
	context.bp();
	if (props.isUsed) {
		context.strokeStyle = usedColor;
		context.fillStyle = usedColor;
	}
	context
		.M(x, y)
		.L(x + w, y)
		.L(x + w, y + h)
		.L(x, y + h)
		.L(x, y);
	context.stroke(null, context.lineWidth * 2);
	for (let i = 0; i < props.numbers.length; i++) {
		CanvasMath.drawInRect(context, {
			batex: props.numbers[i].toString(),
			rect: [x + (i * w) / 2, y, w / 2, h],
		});
	}
	context.restore();
};

// Draws a polyomino given its top left x, y, its shape, its square size, and
// the shadow sizes from ContainerSize (or a Banvas view's this.getShadowSizes).
const drawPolyomino = function(ctx, props) {
	let sv = props.squareSize;
	let sh = props.squareSize;
	const {shape, shadowSizes} = props;
	const blurSize = shadowSizes.inset;
	const colors = props.colors || defaultColors;
	ctx.save();
	ctx.lineWidth = sv / 30;
	ctx.bp().M(props.x, props.y);
	if (props.empty) {
		sv += blurSize / props.shape.length;
		sh += blurSize / props.shape[0].length;
		ctx.M(props.x - blurSize / 2, props.y - blurSize / 2);
	}
	let startI = -1;
	const startJ = 0;
	//Finding where to start (one left of the first filled square on the top row of shape)
	while (!shape[startJ][startI + 1]) {
		ctx.m(sh, 0);
		startI++;
	}
	let currI = startI;
	let currJ = startJ;
	const checkShape = (x, y) => shape[y] && shape[y][x];

	//Tracing around the path of the polyomino one square at a time
	do {
		if (checkShape(currI + 1, currJ - 1) && !checkShape(currI, currJ - 1)) {
			//Up
			ctx.l(0, -sv);
			currJ--;
		} else if (
			checkShape(currI + 1, currJ) &&
			!checkShape(currI + 1, currJ - 1)
		) {
			//Right
			ctx.l(sh, 0);
			currI++;
		} else if (checkShape(currI, currJ) && !checkShape(currI + 1, currJ)) {
			//Down
			ctx.l(0, sv);
			currJ++;
		} else {
			//Left
			ctx.l(-sh, 0);
			currI--;
		}
	} while (currI !== startI || currJ !== startJ);

	ctx.closePath();

	if (props.empty) {
		ctx.save();
		const shadowColor = colors.defaultDarkShadow;

		ctx.clip();
		DrawRectUtil.whiteOutAfterClip(ctx);
		ctx.setShadowBlur(blurSize);
		ctx.shadowColor = shadowColor;
		ctx.lineWidth = blurSize / 2;
		const blurOffset = ctx.lineWidth / 2;
		ctx.shadowOffsetX = blurOffset;
		ctx.shadowOffsetY = blurOffset;
		ctx.strokeStyle = colors.defaultFill;
		ctx.stroke();
		ctx.restore();
	} else {
		ctx.stroke(null, ctx.lineWidth * 2);
		ctx.save();
		ctx.globalAlpha *= props.brighter ? 0.8 : 0.5;
		ctx.fill();
		ctx.restore();
		if (props.forHome) {
			ctx.globalAlpha *= 0.5;
			if (props.gridLineAlpha) {
				ctx.globalAlpha *= props.gridLineAlpha;
			}
			//Drawing the gridlines in the polyomino
			ctx.clip();
			ctx.bp();
			for (let i = 0; i < props.shape[0].length; i++) {
				ctx.M(props.x + i * sh, props.y).l(0, props.shape.length * sv);
			}
			for (let j = 0; j < props.shape.length; j++) {
				ctx.M(props.x, props.y + j * sv).l(props.shape[0].length * sh, 0);
			}
			ctx.stroke(null, ctx.lineWidth / 2);
		}
	}
	ctx.restore();
};

//Used for icons that should appear inset like the stars on the star progress bar
//settings is an optional object with keys shadow, highlight, mainFilled, and main to
// supply different colors and key lessPadding to make the offsets smaller
const drawInsetIcon = function(
	context,
	char,
	filledIn,
	x,
	y,
	w,
	h,
	settings = {}
) {
	const {
		shadow = defaultColors.starBarShadow,
		highlight = defaultColors.starBarHighlight,
		main = defaultColors.starBarMain,
		mainFilled = defaultColors.starProgressFill,
		insetSize = "medium",
	} = settings;

	const drawIcon = r =>
		CanvasMath.drawInRect(context, {
			batex: char,
			rect: r,
			distort: true,
			fontInfo: {family: ["BAOnlineIcons"]},
		});

	context.save();
	//Choose the shadow and highlight rects based on the inset size
	let shadowRect = [x - 1, y - 2, w + 2, h];
	if (insetSize === "small") {
		shadowRect = [x - 0.75, y - 1.5, w + 1.5, h + 0.6];
	} else if (insetSize === "tiny") {
		shadowRect = [x - 0.5, y - 1, w + 1, h + 0.25];
	}
	let highlightRect = [x + 1, y + 2, w, h];
	if (insetSize === "small" || insetSize === "tiny") {
		highlightRect = [x, y + 1, w, h];
	}
	//Draw the shadow, highlight and normal rects.
	context.fillStyle = shadow;
	drawIcon(shadowRect);
	context.fillStyle = highlight;
	drawIcon(highlightRect);
	context.fillStyle = filledIn ? mainFilled : main;
	drawIcon([x, y, w, h]);
	context.restore();
};

const drawPushedButton = function(ctx, rect, props) {
	const colors = props.colors || defaultColors;
	const fillColor = props.fillColor || colors.defaultFill;
	ctx.save();
	ctx.fillStyle = fillColor;
	ctx.strokeStyle = colors.lightGrayFill;
	ctx.bp().rect(rect[0], rect[1], rect[2], rect[3]);
	if (props.drawFunction) {
		ctx.fill();
		props.drawFunction(rect);
	} else {
		if (fillColor === "#FFFFFF") {
			ctx.stroke();
		}
		ctx.fill();
	}
	ctx.restore();
};

// shadowSizes or blurOffset are required on props.
const drawRaisedButton = function(ctx, rect, props) {
	const colors = props.colors || defaultColors;
	const fillColor = props.fillColor || colors.defaultFill;
	ctx.save();
	const shadowColor = props.shadowColor || colors.defaultShadow;
	const blurOffset = props.blurOffset || props.shadowSizes.button;
	ctx.save();
	ctx.shadowColor = shadowColor;
	ctx.shadowOffsetX = blurOffset;
	ctx.shadowOffsetY = blurOffset;
	ctx.setShadowBlur(blurOffset * 4);
	ctx.strokeStyle = colors.lightGrayFill;
	ctx.fillStyle = fillColor;
	ctx.bp().rect(rect[0], rect[1], rect[2], rect[3]);
	if (fillColor === "#FFFFFF" && !props.drawFunction) {
		ctx.stroke();
	}
	ctx.fill();
	ctx.restore();
	if (props.drawFunction) {
		ctx.bp().rect(rect[0], rect[1], rect[2], rect[3]);
		props.drawFunction(rect);
	}
	ctx.restore();
};

const drawBorderedNumberCircle = function(ctx, rect, num, props = {}) {
	const colors = props.colors || defaultColors;
	ctx.beginPath();
	ctx.save();
	ctx.strokeStyle = props.borderColor || colors.inputText;
	if (props.lineWidth) ctx.lineWidth = props.lineWidth;
	ctx.fillStyle = props.backgroundColor || colors.white;
	const left = rect[0];
	const top = rect[1];
	const radius = rect[2] / 2;
	ctx.arc(left + radius, top + radius, radius, 0, 2 * Math.PI);
	ctx.fill();
	ctx.stroke();
	ctx.restore();
	ctx.closePath();
	const numberRect = Math2D.padRectProportional(
		[left, top, radius * 2, radius * 2],
		-0.1,
		-0.1,
		-0.1,
		-0.1
	);
	CanvasMath.drawInRect(ctx, {
		batex: num,
		rect: numberRect,
	});
};

const drawChosen = function(ctx, rect, props = {}) {
	const fontSize = Font.getSize();
	const colors = props.colors || defaultColors;
	const padding = props.noPadding ? 0 : fontSize / 3;
	const r = Math2D.rectToObj(Math2D.padRect(rect, padding));
	//Trying to add some variety to chosen drawing
	ctx.save();
	ctx.strokeStyle = props.circleColor || colors.inputText;
	const lineWidth = Math.min(5, Math.min(r.w, r.h) / 20);
	ctx.lineWidth = lineWidth;
	ctx.lineCap = "round";
	ctx.beginPath();
	ctx.moveTo(r.x + r.w / 4, r.y + padding / 2);
	ctx.bezierCurveTo(
		r.x - r.w / 1.8,
		r.y + r.h,
		r.x + r.w - padding / 2,
		r.y + r.h * 1.3,
		r.x + r.w - padding / 2,
		r.y + r.h / 2
	);
	ctx.bezierCurveTo(
		r.x + r.w,
		r.y,
		r.x + r.w / 5,
		r.y - r.h / 10,
		r.x + r.w / 6,
		r.y
	);
	ctx.stroke();
	ctx.restore();
	return lineWidth;
};

const drawMissingChosen = function(ctx, rect, props = {}) {
	const fontSize = Font.getSize();
	const colors = props.colors || defaultColors;
	const padding = props.noPadding ? 0 : fontSize / 3;
	const r = Math2D.rectToObj(Math2D.padRect(rect, padding));
	ctx.save();
	ctx.strokeStyle = props.circleColor || colors.inputText;
	ctx.lineWidth = Math.min(5, Math.min(r.w, r.h) / 20);
	ctx.setLineDash([ctx.lineWidth * 2, ctx.lineWidth * 3]);
	ctx.lineWidth *= 0.75;
	ctx.lineCap = "round";
	ctx.globalAlpha = 0.5;
	drawRoundedRect(ctx, r.x, r.y, r.w, r.h, fontSize);
	ctx.stroke();
	ctx.restore();
};

const drawEliminatedChoice = function(ctx, rect, props = {}) {
	const fontSize = Font.getSize();
	const colors = props.colors || defaultColors;
	const padding = props.noPadding ? 0 : fontSize / 3;
	const r = Math2D.rectToObj(Math2D.padRect(rect, padding));
	//Trying to add some variety to chosen drawing
	ctx.save();
	ctx.strokeStyle = props.xColor || colors.inputText;
	const defaultLineWidth = Math.min(5, Math.min(r.w, r.h) / 20);
	ctx.lineWidth = props.lineWidthFactor
		? defaultLineWidth * props.lineWidthFactor
		: defaultLineWidth;
	ctx.lineCap = "round";
	ctx.beginPath();
	ctx.moveTo(r.x + r.w * 0.15, r.y + r.h * 0.15);
	ctx.lineTo(r.x + r.w * 0.85, r.y + r.h * 0.85);
	ctx.moveTo(r.x + r.w * 0.15, r.y + r.h * 0.85);
	ctx.lineTo(r.x + r.w * 0.85, r.y + r.h * 0.15);
	ctx.stroke();
	ctx.restore();
};

const drawImageWithShadow = function(ctx, image, rect, props = {}) {
	const colors = props.colors || defaultColors;
	let size = getDraggingShadowSize(rect);
	if (props.shadowMultiplier) {
		size *= props.shadowMultiplier;
	}
	const shadowColor = props.shadowColor || colors.defaultShadow;
	const blurSize = size / 1.2;
	const blurOffset = size;
	ctx.setShadowBlur(blurSize / 2);
	ctx.shadowColor = shadowColor;
	ctx.shadowOffsetX = blurOffset;
	ctx.shadowOffsetY = blurOffset;
	ctx.drawImage(image, rect[0], rect[1], rect[2], rect[3]);
};

// Draws dashed lines indicating axes of symmetry over a rect.
// numberTicks is how many dashes the line should contain.
// emptyLengthRatio is the length of space between dashes divided by the length of a dash.
const drawSymmetryLine = function(
	context,
	rect,
	lineType,
	numberTicks,
	emptyLengthRatio,
	contextProps
) {
	if (lineType) {
		const diagonalLength = Math.sqrt(rect.w * rect.w + rect.h * rect.h);
		const lengthRatio = 1 / ((numberTicks - 1) * (emptyLengthRatio + 1) + 1);
		if (contextProps) {
			context.save();
			_.each(contextProps, (value, key) => {
				context[key] = value;
			});
		}
		context.beginPath();
		switch (lineType) {
			case "horizontal":
				context.setLineDash([
					rect.w * lengthRatio,
					rect.w * lengthRatio * emptyLengthRatio,
				]);
				context.moveTo(rect.x, rect.y + rect.h / 2);
				context.lineTo(rect.x + rect.w, rect.y + rect.h / 2);
				break;
			case "vertical":
				context.setLineDash([
					rect.h * lengthRatio,
					rect.h * lengthRatio * emptyLengthRatio,
				]);
				context.moveTo(rect.x + rect.w / 2, rect.y);
				context.lineTo(rect.x + rect.w / 2, rect.y + rect.h);
				break;
			case "diagonal":
				context.setLineDash([
					diagonalLength * lengthRatio,
					diagonalLength * lengthRatio * emptyLengthRatio,
				]);
				context.moveTo(rect.x, rect.y);
				context.lineTo(rect.x + rect.w, rect.y + rect.h);
				break;
			case "antidiagonal":
				context.setLineDash([
					diagonalLength * lengthRatio,
					diagonalLength * lengthRatio * emptyLengthRatio,
				]);
				context.moveTo(rect.x + rect.w, rect.y);
				context.lineTo(rect.x, rect.y + rect.h);
				break;
		}
		context.stroke();
		if (contextProps) {
			context.restore();
		}
	}
};

// Draws red dot at center of a rect with a rotation arrow around it.
const drawRotationCenter = function(
	context,
	centerX,
	centerY,
	arrowRadius,
	forceColor
) {
	const color = forceColor || defaultColors.incorrectFill;
	context.save();
	context.fillStyle = color;
	context.strokeStyle = color;
	context.lineWidth = context.lineWidth * 2;
	context.beginPath();
	context.arc(centerX, centerY, arrowRadius / 5, 0, 2 * Math.PI);
	context.fill();
	context.beginPath();
	context.moveTo(centerX, centerY - arrowRadius);
	context.arc(centerX, centerY, arrowRadius, -Math.PI / 2, Math.PI / 2);
	context.save();
	context.setLineDash([5, 5]);
	context.stroke();
	context.restore();
	ArrowDraw.drawArrowHead(
		context,
		ArrowDraw.calculateArrowHeadPositions({
			startX: centerX + 5,
			startY: centerY + arrowRadius - 1,
			endX: centerX + 1,
			endY: centerY + arrowRadius,
			headHeight: 5,
			headLength: 5,
			curved: false,
		})
	);
	context.restore();
};

//Draws an image that repeats horizontally only over the width of the context
//repeat is the number of times to repeat the image
const drawRepeatingBackgroundImage = function(context, image, repeat, ratio) {
	const width = Math.floor(context.getWidth() / repeat);
	const height = Math.floor(width * ratio);
	for (let t = 0; t < repeat + 1; t++) {
		context.drawImage(image, t * width, 0, width + 1, height);
	}
};

//Draws the yellow background for level/star progress bar
//isExpandable = true draws and extra nub on bottom for the expandable tab
const drawTopBarYellow = function(context, x = 0, y = 0, w, h, isExpandable) {
	if (!w) {
		w = context.getWidth();
	}
	if (!h) {
		h = context.getHeight();
	}
	const wartWidth = h / 2;
	context.save();
	context.shadowOffsetX = 3;
	context.shadowOffsetY = 3;
	context.shadowColor = "rgba(0,0,0,0.5)";

	//Shape of the yellow thing
	context.beginPath();
	context.moveTo(x + w * 0.03, y);
	context.lineTo(x, y + h - h * 0.02);
	if (isExpandable) {
		// Try to match with TopBar.css .tab
		context.lineTo(x + w / 2 - wartWidth / 2, y + h - h * 0.028);
		context.lineTo(
			x + w / 2 - wartWidth / 2 - wartWidth * 0.05,
			y + h + wartWidth * 0.78
		);
		context.lineTo(
			x + w / 2 + wartWidth / 2 + wartWidth * 0.04,
			y + h + wartWidth * 0.73
		);
		context.lineTo(x + w / 2 + wartWidth / 2, y + h - h * 0.032);
	}
	context.lineTo(x + w, y + h - h * 0.04);
	context.lineTo(x + w - w * 0.022, y);
	context.closePath();
	context.clip();
	context.clearRect(0, 0, w, h);
	context.rect(x + w, y, w, h).fill(0xfdc441);

	const userAgent = global.navigator && global.navigator.userAgent;
	// Windows Firefox does not like this image background getting clipped so it
	// just doesn't clip it. Don't draw image bg if Windows Firefox or unknown browser
	if (browser && !(browser.name === "firefox" && /Windows/.test(userAgent))) {
		const bgImage = ImageCache.src(
			global.BASE_URL + "assets/images/elements/argyles/patch-yellow.png"
		);
		drawRepeatingBackgroundImage(context, bgImage, 4.5, 120 / 69.3);
	}
	context.restore();
};

const drawStarBarSections = function(context, rect, isFirst, isLast, trimOnly) {
	const lw = rect.h / 35; //lineWidth
	//Add buffers for lineWidth
	const x = rect.x + 2 * lw;
	const y = rect.y + 2 * lw;
	const w = rect.w - 4 * lw;
	const h = rect.h - 3 * lw;
	const gapSize = Math.floor(h / 8);

	let p1, p2, p3, p4;
	context.save();
	if (trimOnly) {
		context.globalAlpha = 0.7;
	}
	if (isFirst) {
		p1 = {x, y: y + h};
		p2 = {x: x + Math.floor(gapSize * 1.7), y};
		p3 = {x: x + w - Math.floor(gapSize / 2), y};
		p4 = {x: x + w - Math.floor(gapSize / 2), y: y + h};
		//fill in base color
		if (!trimOnly) {
			context
				.bp()
				.M(p1.x, p1.y)
				.L(p2.x, p2.y)
				.L(p3.x, p3.y)
				.L(p4.x, p4.y)
				.Z()
				.fill(defaultColors.starBarMain);
		}
		//shadow on upper left
		context.lineWidth = lw * 2;
		context
			.bp()
			.M(p1.x, p1.y)
			.L(p2.x, p2.y)
			.L(p3.x + lw / 2, p3.y)
			.stroke(defaultColors.starBarShadow);
		//small shadow on right
		context.lineWidth = lw;
		context
			.bp()
			.M(p3.x, p3.y)
			.L(p4.x, p4.y)
			.stroke(defaultColors.starBarShadow);
		//highlight on bottom
		context.lineWidth = lw * 2;
		context.strokeStyle = defaultColors.starBarHighlight;
		context
			.bp()
			.M(p4.x, p4.y)
			.L(p1.x, p1.y)
			.stroke(defaultColors.starBarHighlight);
	} else if (isLast) {
		p1 = {x: x + Math.floor(gapSize / 2), y: y + h};
		p2 = {x: x + Math.floor(gapSize / 2), y};
		p3 = {x: x + w - gapSize * 2.7, y};
		p4 = {x: x + w, y: y + h};
		//fill in base color
		if (!trimOnly) {
			context
				.bp()
				.M(p1.x, p1.y)
				.L(p2.x, p2.y)
				.L(p3.x, p3.y)
				.L(p4.x, p4.y)
				.Z()
				.fill(defaultColors.starBarMain);
		}
		//shadow on upper right
		context.lineWidth = lw * 2;
		context
			.bp()
			.M(p2.x, p2.y)
			.L(p3.x, p3.y)
			.L(p4.x, p4.y)
			.stroke(defaultColors.starBarShadow);
		//highlight on bottom
		context.lineWidth = lw * 2;
		context
			.bp()
			.M(p4.x, p4.y)
			.L(p1.x, p1.y)
			.stroke(defaultColors.starBarHighlight);
	} else {
		p1 = {x: x + Math.floor(gapSize / 2), y: y + h};
		p2 = {x: x + Math.floor(gapSize / 2), y};
		p3 = {x: x + w - Math.floor(gapSize / 2), y};
		p4 = {x: x + w - Math.floor(gapSize / 2), y: y + h};
		//fill in base color
		if (!trimOnly) {
			context
				.bp()
				.M(p1.x, p1.y)
				.L(p2.x, p2.y)
				.L(p3.x, p3.y)
				.L(p4.x, p4.y)
				.Z()
				.fill(defaultColors.starBarMain);
		}
		//shadow on upper left
		context.lineWidth = lw * 2;
		context
			.bp()
			.M(p2.x, p2.y)
			.L(p3.x + lw / 2, p3.y)
			.stroke(defaultColors.starBarShadow);
		//small shadow on right
		context.lineWidth = lw;
		context
			.bp()
			.M(p3.x, p3.y)
			.L(p4.x, p4.y)
			.stroke();
		//highlight on bottom
		context.lineWidth = lw * 2;
		context
			.bp()
			.M(p4.x, p4.y)
			.L(p1.x, p1.y)
			.stroke(defaultColors.starBarHighlight);
	}
	context.restore();
};

// Returns an object specifying the positions of the circles and outer rectangle
// in the Venn diagram. The object has the following 5 properties:
// - leftCircle: The center of the left, or A, circle, as an array [x,y]
// - rightCircle: The center of the right, or B, circle, as an array [x,y]
// - bottomCircle: The center of the bottom, or C, circle, as an array [x,y]
// - universalRectangle: The position, width, and height of the outer rectangle,
//   as an array [x,y,w,h]. This will still be set even if universallOff is true.
// - circlesToLeftEdgeDist: The distance from the circles to the left edge of the
//   outer rectangle.
// If universalOff is true or numCircles is less than 3, some of the properties
// (circlesToLeftEdgeDist, rightCircle, or bottomCircle) will be meaningless and
// are expected to remain unused.
const getVennDiagramPositions = function(
	radius,
	centerDelta,
	pad,
	lineWidth,
	universalOff,
	numCircles,
	getOffsetCoords,
	bIsSubset
) {
	bIsSubset = !!bIsSubset;

	// Distances from the circles to the top/bottom, left, and right edges of
	// the outer rectangle respectively. This does not include any padding.
	const outer = [radius * 0.1, radius * 0.9, radius * 0.05];
	// The "base" width and height of the Venn diagram.
	const size = [
		outer[1] + 2 * radius + centerDelta + outer[2],
		2 * (outer[0] + radius),
	];
	// If there are three circles, the third one increases the total height by
	// the radius.
	const borderHeight = size[1] - lineWidth + (numCircles === 3 ? radius : 0);

	const lineWidthOffset = getOffsetCoords(lineWidth, lineWidth);
	// The total width of the Venn diagram.
	const width = size[0] + 2 * lineWidth + pad;

	// leftCircle, rightCircle, and bottomCircle represent circles A, B, and C
	// respectively. bottomCircle will be unused if there are 1 or 2 circles,
	// and rightCircle will be unused if there is only 1 circle.
	let leftCircle;
	let rightCircle;
	let bottomCircle;
	if (universalOff) {
		// If there is only one circle, then it should be centered.
		if (numCircles === 1) {
			leftCircle = getOffsetCoords(width / 2, outer[0] + radius);
		} else {
			leftCircle = getOffsetCoords(
				width / 2 - centerDelta / 2,
				outer[0] + radius
			);
		}
		rightCircle = getOffsetCoords(
			width / 2 + centerDelta / 2,
			outer[0] + radius
		);
		bottomCircle = getOffsetCoords(width / 2, outer[0] + 2 * radius);
	} else {
		// If there is only one circle, place it on the right side of the outer
		// rectangle.
		if (numCircles === 1) {
			leftCircle = getOffsetCoords(outer[1] + 2 * radius, outer[0] + radius);
		} else {
			leftCircle = getOffsetCoords(outer[1] + radius, outer[0] + radius);
		}
		rightCircle = getOffsetCoords(
			outer[1] + radius + centerDelta,
			outer[0] + radius
		);
		bottomCircle = getOffsetCoords(
			outer[1] + radius + 0.5 * centerDelta,
			outer[0] + 2 * radius
		);
	}
	const universalRectangle = [
		lineWidthOffset[0],
		lineWidthOffset[1],
		size[0] - lineWidth + pad,
		borderHeight,
	];
	const circlesToLeftEdgeDist = outer[1];
	return {
		leftCircle,
		rightCircle,
		bottomCircle,
		universalRectangle,
		circlesToLeftEdgeDist,
	};
};

const drawVennDiagram = function(ctx, props) {
	/* derived constants */
	const cS = ColorUtil.getCurrentScheme();
	const fontHeight = parseInt(ctx.font);
	const lineWidth = ctx.lineWidth;

	/* constants */
	const cornerRadius = 20;
	const itemWidthScalar = 0.7;
	const itemHeightScalar = 1.2;

	/* read props */
	const normalizeText = !!props.normalizeText;
	const oneLineTitles = !!props.oneLineTitles;
	const centerDeltaFactor = props.centerDeltaFactor || 1;
	const radius = props.radius || 90;
	const pad = props.padding || 5;
	// Number of circles, defaulting to 2. Can be 1, 2, or 3.
	const numCircles = props.numCircles || 2;
	// If true, does not allocate any space for a universal region.
	const universalOff = props.universalOff || false;
	// If true, does not draw universal border around entire diagram.
	const noUniversalBorder = props.noUniversalBorder || props.universalOff;
	const shaded = props.shaded || [];
	const labels = props.labels || {};
	const boundaryLabels = props.boundaryLabels || {};
	const diagramData = props.diagramData || {};
	const bIsSubset = !!props.bIsSubset;
	const isDiagram = props.isDiagram || false;
	const fontFamily = props.fontFamily || ["Roboto"];
	// Height of text boxes relative to height of circle.
	const textHeightFactor = props.textHeightFactor || 0.3;
	// Function that returns coordinates in the context given an x offset and y offset.
	const getOffsetCoords = props.getOffsetCoords;
	// Only used to potentially color one zone blue.
	const selectedZone = props.selectedZone;

	/* calculated constants */
	const textHeight = normalizeText ? radius * textHeightFactor : fontHeight;
	// Distance between centers of the two circles.
	const centerDelta = radius * centerDeltaFactor;

	const {
		leftCircle,
		rightCircle,
		bottomCircle,
		universalRectangle,
		circlesToLeftEdgeDist,
	} = getVennDiagramPositions(
		radius,
		centerDelta,
		pad,
		lineWidth,
		universalOff,
		numCircles,
		getOffsetCoords,
		bIsSubset
	);
	const padCoords = getOffsetCoords(pad, pad);
	// Length of the lines from circles to boundary labels, if they exist.
	const boundaryLength = 4 * pad;
	const shouldDrawBoundaryLabels =
		universalOff && boundaryLabels && !_.isEmpty(boundaryLabels);
	// Diagrams need this, but the interactive Venn diagram engine does not.
	if (isDiagram) {
		const diagramWidth = universalRectangle[2] + 3 * lineWidth;
		let diagramHeight = universalRectangle[3] + 3 * lineWidth;
		// Add extra room if there's a boundary label on the bottom circle.
		// The amount of extra room is very arbitrary, like some other values
		// involved in Venn diagram positioning - someday, it might be good to
		// modify the Venn diagram code to not rely on hardcoded values.
		if (shouldDrawBoundaryLabels && numCircles === 3) {
			diagramHeight += 0.75 * radius;
		}
		ctx.setSize(diagramWidth, diagramHeight);
	}

	const getShadeColor = function(zone, shadeIndex) {
		let layeredShadingColors = [
			cS.vennLayer1,
			cS.vennLayer2,
			cS.vennLayer3,
			cS.vennLayer4,
		];
		if (!props.progressiveShading) {
			layeredShadingColors = [
				cS.lightGrayFill,
				cS.lightGrayFill,
				cS.lightGrayFill,
				cS.lightGrayFill,
			];
		}
		let color =
			shaded.indexOf(zone) > -1
				? layeredShadingColors[shadeIndex]
				: cS.defaultFill;
		if (selectedZone === zone) {
			color = cS.vennHoverBlue;
		}
		return color;
	};

	// Shade in areas with their appropriate colors.
	if (Array.isArray(shaded)) {
		if (bIsSubset) {
			ctx.save();

			// shade universal region
			if (!universalOff) {
				ctx.fillStyle = getShadeColor("universal", 0);
				drawRoundedRect(ctx, ...universalRectangle, cornerRadius);
				ctx.fill();
			}

			// shade A ellipse
			ctx.fillStyle = getShadeColor("A", 1);
			ctx.bp();
			// prevent breakage on tests (where ctx.ellipse DNE and ctx.methodName is usually a spy)
			ctx.context &&
				ctx.context.ellipse &&
				ctx.context.ellipse(
					(leftCircle[0] + rightCircle[0]) / 2,
					leftCircle[1],
					1.6 * radius,
					radius,
					0,
					0,
					2 * Math.PI
				);
			ctx.fill();

			// shade B circle
			ctx.fillStyle = getShadeColor("B", 1);
			ctx
				.bp()
				.arc(rightCircle[0], rightCircle[1], radius * 0.8, 0, 2 * Math.PI)
				.fill();

			ctx.restore();
		} else {
			// else !bIsSubset
			const intersectionsAB = Math2D.circleIntersections(
				leftCircle[0],
				leftCircle[1],
				radius,
				rightCircle[0],
				rightCircle[1],
				radius
			);
			const intersectionsBC = Math2D.circleIntersections(
				rightCircle[0],
				rightCircle[1],
				radius,
				bottomCircle[0],
				bottomCircle[1],
				radius
			);
			const intersectionsCA = Math2D.circleIntersections(
				leftCircle[0],
				leftCircle[1],
				radius,
				bottomCircle[0],
				bottomCircle[1],
				radius
			);

			const angABtopA = Math.atan2(
				intersectionsAB[1][1] - leftCircle[1],
				intersectionsAB[1][0] - leftCircle[0]
			);
			const angABbottomA = Math.atan2(
				intersectionsAB[0][1] - leftCircle[1],
				intersectionsAB[0][0] - leftCircle[0]
			);
			const angABtopB = Math.atan2(
				intersectionsAB[1][1] - rightCircle[1],
				intersectionsAB[1][0] - rightCircle[0]
			);
			const angABbottomB = Math.atan2(
				intersectionsAB[0][1] - rightCircle[1],
				intersectionsAB[0][0] - rightCircle[0]
			);

			const angBCrightC = Math.atan2(
				intersectionsBC[1][1] - bottomCircle[1],
				intersectionsBC[1][0] - bottomCircle[0]
			);
			const angBCleftC = Math.atan2(
				intersectionsBC[0][1] - bottomCircle[1],
				intersectionsBC[0][0] - bottomCircle[0]
			);
			const angBCrightB = Math.atan2(
				intersectionsBC[1][1] - rightCircle[1],
				intersectionsBC[1][0] - rightCircle[0]
			);
			const angBCleftB = Math.atan2(
				intersectionsBC[0][1] - rightCircle[1],
				intersectionsBC[0][0] - rightCircle[0]
			);

			const angCArightA = Math.atan2(
				intersectionsCA[1][1] - leftCircle[1],
				intersectionsCA[1][0] - leftCircle[0]
			);
			const angCAleftA = Math.atan2(
				intersectionsCA[0][1] - leftCircle[1],
				intersectionsCA[0][0] - leftCircle[0]
			);
			const angCArightC = Math.atan2(
				intersectionsCA[1][1] - bottomCircle[1],
				intersectionsCA[1][0] - bottomCircle[0]
			);
			const angCAleftC = Math.atan2(
				intersectionsCA[0][1] - bottomCircle[1],
				intersectionsCA[0][0] - bottomCircle[0]
			);

			ctx.save();
			if (!universalOff) {
				ctx.fillStyle = getShadeColor("universal", 0);
				drawRoundedRect(ctx, ...universalRectangle, cornerRadius);
				ctx.fill();
			}

			ctx.fillStyle = getShadeColor("A", 1);
			ctx
				.bp()
				.arc(leftCircle[0], leftCircle[1], radius, 0, 2 * Math.PI)
				.fill();

			if (numCircles >= 2) {
				ctx.fillStyle = getShadeColor("B", 1);
				ctx
					.bp()
					.arc(rightCircle[0], rightCircle[1], radius, 0, 2 * Math.PI)
					.fill();
			}

			if (numCircles === 3) {
				ctx.fillStyle = getShadeColor("C", 1);
				ctx
					.bp()
					.arc(bottomCircle[0], bottomCircle[1], radius, 0, 2 * Math.PI)
					.fill();
			}

			if (numCircles >= 2) {
				ctx.fillStyle = getShadeColor("AB", 2);
				ctx
					.bp()
					.arc(
						rightCircle[0],
						rightCircle[1],
						radius,
						angABtopB,
						angABbottomB,
						true
					);
				ctx
					.arc(
						leftCircle[0],
						leftCircle[1],
						radius,
						angABbottomA,
						angABtopA,
						true
					)
					.fill();
			}

			if (numCircles === 3) {
				ctx.fillStyle = getShadeColor("BC", 2);
				ctx
					.bp()
					.arc(
						rightCircle[0],
						rightCircle[1],
						radius,
						angBCleftB,
						angBCrightB,
						true
					);
				ctx
					.arc(
						bottomCircle[0],
						bottomCircle[1],
						radius,
						angBCrightC,
						angBCleftC,
						true
					)
					.fill();

				ctx.fillStyle = getShadeColor("CA", 2);
				ctx
					.bp()
					.arc(
						leftCircle[0],
						leftCircle[1],
						radius,
						angCAleftA,
						angCArightA,
						true
					);
				ctx
					.arc(
						bottomCircle[0],
						bottomCircle[1],
						radius,
						angCArightC,
						angCAleftC,
						true
					)
					.fill();

				ctx.fillStyle = getShadeColor("ABC", 3);
				ctx
					.bp()
					.arc(
						leftCircle[0],
						leftCircle[1],
						radius,
						angABbottomA,
						angCArightA,
						true
					);
				ctx.arc(
					bottomCircle[0],
					bottomCircle[1],
					radius,
					angCArightC,
					angBCleftC,
					true
				);
				ctx
					.arc(
						rightCircle[0],
						rightCircle[1],
						radius,
						angBCleftB,
						angABbottomB,
						true
					)
					.fill();
			}
			ctx.restore();
		}
	}

	/* Draw the outlines */
	// draw top left circle (or ellipse if every element in B is in A)
	if (bIsSubset) {
		ctx.bp();
		// prevent breakage on tests (where ctx.ellipse DNE and ctx.methodName is usually a spy)
		ctx.context &&
			ctx.context.ellipse &&
			ctx.context.ellipse(
				(leftCircle[0] + rightCircle[0]) / 2,
				leftCircle[1],
				1.6 * radius,
				radius,
				0,
				0,
				2 * Math.PI
			);
		ctx.stroke();
	} else {
		ctx
			.bp()
			.arc(leftCircle[0], leftCircle[1], radius, 0, 2 * Math.PI)
			.stroke();
	}
	if (numCircles >= 2) {
		// if subset, make circle smaller
		if (bIsSubset) {
			ctx
				.bp()
				.arc(rightCircle[0], rightCircle[1], radius * 0.8, 0, 2 * Math.PI)
				.stroke();
		} else {
			ctx
				.bp()
				.arc(rightCircle[0], rightCircle[1], radius, 0, 2 * Math.PI)
				.stroke();
		}
	}
	if (numCircles === 3) {
		ctx
			.bp()
			.arc(bottomCircle[0], bottomCircle[1], radius, 0, 2 * Math.PI)
			.stroke();
	}

	// Draw the rounded rectangular boundary
	if (!noUniversalBorder) {
		drawRoundedRect(ctx, ...universalRectangle, cornerRadius);
		ctx.stroke();
	}

	// Draw the category labels
	if (!universalOff) {
		CanvasMath.drawInRect(ctx, {
			scaleDownOnly: true,
			batex: labels.universal || "",
			rect: [
				padCoords[0] + radius / 9,
				padCoords[1],
				circlesToLeftEdgeDist,
				textHeight,
			],
			...(oneLineTitles && {wrapWidth: Infinity}),
		});
	}

	let textWidth; // width of roughly one text box
	// the following were found via guess-and-check for what looks good
	if (numCircles === 1) {
		textWidth = radius * 1.2;
	} else if (bIsSubset) {
		textWidth = radius * 0.7;
	} else {
		textWidth = radius * 0.65;
	}
	const firstProps = {
		scaleDownOnly: true,
		batex: labels.A || "",
		rect: [
			leftCircle[0] - (radius * 7) / 10 - (textWidth * 1) / 5,
			leftCircle[1] - (3 / 5) * radius,
			(textWidth * 7) / 5,
			textHeight,
		],
		...(oneLineTitles && {wrapWidth: Infinity}),
	};
	const secondProps = {
		scaleDownOnly: true,
		batex: labels.B || "",
		rect: [
			rightCircle[0] - textWidth / 2 - textWidth / 5,
			rightCircle[1] - (3 / 5) * radius,
			(textWidth * 7) / 5,
			textHeight,
		],
		...(oneLineTitles && {wrapWidth: Infinity}),
	};
	const thirdProps = {
		scaleDownOnly: true,
		batex: labels.A || "",
		fontInfo: {size: 10, family: ["Roboto"]},
		rect: [
			leftCircle[0] - (radius * 6) / 10,
			leftCircle[1] - (radius * 8) / 10,
			textWidth,
			textHeight,
		],
		...(oneLineTitles && {wrapWidth: Infinity}),
	};
	const fourthProps = {
		scaleDownOnly: true,
		batex: labels.B || "",
		rect: [
			rightCircle[0] - (radius * 1) / 11,
			rightCircle[1] - (radius * 7) / 9,
			textWidth,
			textHeight,
		],
		...(oneLineTitles && {wrapWidth: Infinity}),
	};
	const fifthProps = {
		scaleDownOnly: true,
		batex: labels.C || "",
		rect: [
			bottomCircle[0] - (387 / 990) * radius,
			bottomCircle[1] + (2 / 3) * radius,
			radius * 0.8,
			textHeight,
		],
		...(oneLineTitles && {wrapWidth: Infinity}),
	};

	const allProps = [
		firstProps,
		secondProps,
		thirdProps,
		fourthProps,
		fifthProps,
	];

	// Standardizing the text size to the smallest text size out of all of the labels
	const fontInfo = _.extend({}, Font.toInfo(ctx), {});
	const getMeasure = properties => {
		if (properties.batex && properties.batex !== "") {
			const wrapWidth =
				properties.wrapWidth === undefined
					? properties.rect[2]
					: properties.wrapWidth;
			const parsed = FastCanvasMath.parseBatex(properties.batex);
			if (parsed) {
				const measure = FastCanvasMath.baseWrapAndMeasure(
					ctx,
					properties,
					fontInfo,
					wrapWidth,
					parsed
				);
				return measure;
			} else {
				// without wrapping
				const batexMeasure = CanvasMath.measureBatex(ctx, {
					batex: properties.batex,
					fontInfo,
				});
				const measure = {
					finalWidth: batexMeasure.measure.width,
					finalHeight: batexMeasure.measure.height,
				};
				return measure;
			}
		}
		return false;
	};

	let minScalar = Number.MAX_SAFE_INTEGER;
	const checkAndComputeMin = prop => {
		const measured = getMeasure(prop);
		if (measured) {
			const fit = Math2D.fitInRect(
				[0, 0, measured.finalWidth, measured.finalHeight],
				prop.rect,
				{
					scaleOff: prop.scaleOff,
					scaleDownOnly: prop.scaleDownOnly,
					distort: prop.distort,
				}
			);
			// fit.scale[0] and fit.scale[1] are the same
			if (fit.scale[0] < minScalar) {
				minScalar = fit.scale[0];
			}
		}
	};

	// Only check its value if it will be rendered.
	if (bIsSubset) {
		checkAndComputeMin(firstProps);
		if (numCircles >= 2) {
			checkAndComputeMin(secondProps);
		}
	} else {
		checkAndComputeMin(thirdProps);
		if (numCircles >= 2) {
			checkAndComputeMin(fourthProps);
		}
	}
	if (numCircles === 3) {
		checkAndComputeMin(fifthProps);
	}

	// if minScalar has not been set by checkAndComputeMin, then we just set it to 1
	const scalar = minScalar === Number.MAX_SAFE_INTEGER ? 1 : minScalar;

	const minFontInfo = {size: scalar * fontInfo.size, family: fontFamily};

	// Adding the new minimum font to all of the label properties
	_.each(allProps, prop => {
		prop.fontInfo = minFontInfo;
	});

	if (bIsSubset) {
		CanvasMath.drawInRect(ctx, firstProps);
		if (numCircles >= 2) {
			CanvasMath.drawInRect(ctx, secondProps);
		}
	} else {
		CanvasMath.drawInRect(ctx, thirdProps);
		if (numCircles >= 2) {
			CanvasMath.drawInRect(ctx, fourthProps);
		}
	}

	if (numCircles === 3) {
		CanvasMath.drawInRect(ctx, fifthProps);
	}

	const drawRectProps = {
		scaleDownOnly: true,
	};
	if (isDiagram) {
		_.extend(drawRectProps, {overrideBlankSize: ctx.overrideBlankSize});
	}
	if (diagramData) {
		// Draw the items
		_.forEach(diagramData, (value, key) => {
			if (_.isArray(value)) {
				value = value.join("\n");
			}
			let rect = [0, 0, 0, 0];
			if (numCircles === 3) {
				if (!universalOff && key === "universal") {
					//universal only
					rect = [
						padCoords[0],
						getOffsetCoords(0, pad + textHeight)[1],
						circlesToLeftEdgeDist - 4 * pad,
						2 * radius - 4 * pad,
					];
				} else if (key === "A") {
					//category A only
					rect = [
						leftCircle[0] - 0.8 * radius,
						leftCircle[1] - (13 / 18) * radius,
						radius * itemWidthScalar,
						radius * itemHeightScalar,
					];
				} else if (key === "AB") {
					//category A and B
					rect = [
						bottomCircle[0] - 0.35 * radius,
						rightCircle[1] - (8 / 9) * radius,
						centerDelta * itemWidthScalar,
						radius,
					];
				} else if (key === "B") {
					//category B only
					rect = [
						rightCircle[0] + 0.1 * radius,
						rightCircle[1] - (13 / 18) * radius,
						radius * itemWidthScalar,
						radius * itemHeightScalar,
					];
				} else if (key === "BC") {
					//category B and C only
					rect = [
						bottomCircle[0] + (21.75 / 90) * radius,
						bottomCircle[1] - (78.5 / 90) * radius,
						radius * itemWidthScalar,
						radius * itemHeightScalar,
					];
				} else if (key === "C") {
					//category C only
					rect = [
						bottomCircle[0] - 0.35 * radius,
						bottomCircle[1] - (8 / 45) * radius,
						radius * itemWidthScalar,
						radius * itemHeightScalar,
					];
				} else if (key === "CA") {
					//category C and A
					rect = [
						bottomCircle[0] - (83.75 / 90) * radius,
						bottomCircle[1] - (78.5 / 90) * radius,
						radius * itemWidthScalar,
						radius * itemHeightScalar,
					];
				} else if (key === "ABC") {
					//category A, B, and C
					rect = [
						bottomCircle[0] - 0.35 * radius,
						bottomCircle[1] - (37 / 30) * radius,
						radius * itemWidthScalar,
						radius * itemHeightScalar,
					];
				}
			} else if (numCircles === 2) {
				if (!universalOff && key === "universal") {
					//universal only
					rect = [
						padCoords[0],
						getOffsetCoords(0, 3 * pad + textHeight)[1],
						bIsSubset ? radius * 0.7 : circlesToLeftEdgeDist - 2 * pad,
						2 * radius - 4 * pad,
					];
				} else if (key === "A") {
					//category A only
					if (bIsSubset) {
						rect = [
							leftCircle[0] - 0.8 * radius,
							leftCircle[1] - 0.5 * radius,
							textWidth * 1.2,
							radius * itemHeightScalar,
						];
					} else {
						rect = [
							leftCircle[0] - 0.8 * radius,
							leftCircle[1] - 0.5 * radius,
							radius * itemWidthScalar * centerDeltaFactor,
							radius * itemHeightScalar,
						];
					}
				} else if (key === "AB") {
					//category A and B
					rect = [
						leftCircle[0] + 0.15 * radius,
						leftCircle[1] - 0.5 * radius,
						radius * itemWidthScalar * centerDeltaFactor,
						radius,
					];
				} else if (key === "B") {
					if (bIsSubset) {
						rect = [
							rightCircle[0] - textWidth * 0.6,
							rightCircle[1] - 0.5 * radius,
							textWidth * 1.2,
							radius * itemHeightScalar,
						];
					} else {
						rect = [
							rightCircle[0] + (0.1 + (1 - centerDeltaFactor)) * radius,
							rightCircle[1] - 0.5 * radius,
							radius * itemWidthScalar * centerDeltaFactor,
							radius * itemHeightScalar,
						];
					}
					//category B only
				}
			} else {
				if (!universalOff && key === "universal") {
					//universal only
					rect = [
						padCoords[0],
						getOffsetCoords(0, 3 * pad + textHeight)[1],
						circlesToLeftEdgeDist - 2 * pad,
						2 * radius - 4 * pad,
					];
				} else if (key === "A") {
					//category A only
					rect = [
						leftCircle[0] - 0.35 * radius,
						leftCircle[1] - 0.5 * radius,
						radius * itemWidthScalar,
						radius * itemHeightScalar,
					];
				}
			}
			drawRectProps.batex = value || "";
			drawRectProps.rect = rect;
			CanvasMath.drawInRect(ctx, drawRectProps);
		});
	}

	// Draw boundary labels
	if (shouldDrawBoundaryLabels) {
		_.forEach(boundaryLabels, (value, key) => {
			const rightStart = getOffsetCoords(
				rightCircle[0] + radius * Math.cos(Math.PI / 9),
				rightCircle[1] - radius * Math.sin(Math.PI / 9)
			);
			const rightEnd = getOffsetCoords(
				rightStart[0] + boundaryLength * Math.cos(Math.PI / 3),
				rightStart[1] - boundaryLength * Math.sin(Math.PI / 3)
			);
			const leftStart = getOffsetCoords(
				leftCircle[0] - radius * Math.cos(Math.PI / 9),
				leftCircle[1] - radius * Math.sin(Math.PI / 9)
			);
			const leftEnd = getOffsetCoords(
				leftStart[0] - boundaryLength * Math.cos(Math.PI / 3),
				leftStart[1] - boundaryLength * Math.sin(Math.PI / 3)
			);

			const topLeft = getOffsetCoords(0, 0);
			let rectX = topLeft[0];
			let rectY = topLeft[1];
			const rectWidth = radius * itemWidthScalar;
			const rectHeight = radius * itemHeightScalar;

			ctx.save();
			//draw the appropriate boundary label and its line.
			if (key === "A") {
				ctx.bp().moveTo(leftStart[0], leftStart[1]);
				ctx
					.lineTo(leftEnd[0], leftEnd[1])
					.stroke()
					.Z();

				rectX = leftEnd[0] - 0.45 * radius;
				rectY = leftEnd[1] - 0.75 * radius;
			} else if (numCircles >= 2 && key === "B") {
				ctx.bp().moveTo(rightStart[0], rightStart[1]);
				ctx
					.lineTo(rightEnd[0], rightEnd[1])
					.stroke()
					.Z();

				rectX = rightEnd[0] - 0.25 * radius;
				rectY = rightEnd[1] - 0.75 * radius;
			} else if (numCircles === 3 && key === "C") {
				const bottomStart = getOffsetCoords(
					bottomCircle[0],
					bottomCircle[1] + radius
				);
				const bottomEnd = getOffsetCoords(
					bottomCircle[0],
					bottomCircle[1] + radius + boundaryLength
				);
				ctx.bp().moveTo(bottomStart[0], bottomStart[1]);
				ctx
					.lineTo(bottomEnd[0], bottomEnd[1])
					.stroke()
					.Z();

				rectX = bottomEnd[0] - 0.35 * radius;
				rectY = bottomEnd[1] - 0.45 * radius;
			}

			drawRectProps.batex = value || "";
			drawRectProps.rect = [rectX, rectY, rectWidth, rectHeight];
			CanvasMath.drawInRect(ctx, drawRectProps);
			ctx.restore();
		});
	}
};

const drawDemoBar = function(context, rect, isExpandable) {
	const {x, y, w, h} = rect;

	// Background
	drawTopBarYellow(context, x, y, w, h, isExpandable);

	// Demo text
	const textImage = ImageCache.src(AssetsUtil.getDemo("demo-text"), true);
	const textImageScale = 0.4;
	context.drawImage(
		textImage,
		((1 - textImageScale) * w) / 2,
		((1 - textImageScale) * h) / 2,
		textImageScale * w,
		textImageScale * h
	);

	return {
		w,
		h,
	};
};

// Marks a right angle given by three points p1, p2, and p3, where p2 is the
// vertex. p1, p2, and p3 should be in the form of [x,y] arrays, and props
// contains optional lineWidth and color properties.
// Note that this method doesn't validate that the angle at p2 is actually a
// right angle, so if it isn't, it'll draw some rhombus-shaped angle mark.
const drawRightAngleMark = function(context, p1, p2, p3, props = {}) {
	// Some of this code was taken from ShapeArea.rightAngle. It may be worth
	// eventually refactoring that method to call this helper function.
	const vector21 = V2(p1[0] - p2[0], p1[1] - p2[1]);
	const vector23 = V2(p3[0] - p2[0], p3[1] - p2[1]);
	if (vector21.magnitude() === 0 || vector23.magnitude() === 0) {
		return;
	}

	context.save();
	context.lineWidth = props.lineWidth || context.lineWidth;
	context.strokeStyle = props.color || defaultColors.defaultBlackFill;
	const angleSize = context.lineWidth * 18;
	const vector21Scaled = vector21.scale(angleSize / vector21.magnitude());
	const vector23Scaled = vector23.scale(angleSize / vector23.magnitude());
	const vertex = V2(p2[0], p2[1]);
	context
		.bp()
		.M(vertex.add(vector21Scaled))
		.L(vertex.add(vector21Scaled).add(vector23Scaled))
		.L(vertex.add(vector23Scaled));
	context.stroke();
	context.restore();
};

// Marks the angle given by three points p1, p2, and p3, where p2 is the vertex.
// p1, p2, and p3 should be in the form of [x,y] arrays, and props contains
// optional lineWidth and color properties.
const drawAngleMark = function(context, p1, p2, p3, props = {}) {
	// Some of this code was taken from ShapeArea.markAngle. It may be worth
	// eventually refactoring that method to call this helper function.
	const angle21 = Math.atan2(p1[1] - p2[1], p1[0] - p2[0]);
	const angle23 = Math.atan2(p3[1] - p2[1], p3[0] - p2[0]);
	const isCCW = (angle23 - angle21 + 2 * Math.PI) % (2 * Math.PI) > Math.PI;

	context.save();
	context.lineWidth = props.lineWidth || context.lineWidth;
	context.strokeStyle = props.color || defaultColors.defaultBlackFill;
	const angleSize = context.lineWidth * 18;
	context.bp().arc(p2[0], p2[1], angleSize, angle21, angle23, isCCW);
	context.stroke();
	context.restore();
};

module.exports = {
	drawRoundedRect,
	drawInShape,
	drawTable,
	drawTriangle,
	drawStar,
	getDraggingShadowSize,
	getNonDraggingShadowSizeRatio,
	drawDraggingRect,
	drawDraggable,
	drawDroppedDraggable,
	drawHoveringDroppable,
	drawShadowContent,
	drawGlow,
	drawInnerGlow,
	drawX,
	drawXWithOffset,
	drawHorizontalDomino,
	drawPolyomino,
	drawToothpick,
	drawInsetIcon,
	drawImageWithShadow,
	drawPushedButton,
	drawRaisedButton,
	drawMultiplePattern: MultiplePattern.drawMultiplePattern,
	drawBorderedNumberCircle,
	drawChosen,
	drawMissingChosen,
	drawEliminatedChoice,
	drawSymmetryLine,
	drawRotationCenter,
	drawRepeatingBackgroundImage,
	drawTopBarYellow,
	drawStarBarSections,
	getVennDiagramPositions,
	drawVennDiagram,
	drawDemoBar,
	drawRightAngleMark,
	drawAngleMark,
	drawRegularShape,
};
